import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from '@/pages/shared/base-query-auth';
import { runMutationOnce, runQueryOnce, runTableQuery } from '@/pages/shared/rtk-query-helpers';

const toFormData = (fields) => {
    const formData = new FormData();

    Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
    });

    return formData;
};

export const documentNumberTypeApi = createApi({
    reducerPath: 'documentNumberTypeApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getTableData: builder.mutation({
            query: (data) => ({ url: '/api/utilities/numberTypes/list', method: 'POST', body: data }),
        }),
        createNumberType: builder.mutation({
            query: (data) => ({
                url: '/api/utilities/numberTypes/create',
                method: 'POST',
                body: { ...data, numberTypeId: 0 },
            }),
        }),
        updateNumberType: builder.mutation({
            query: (data) => ({ url: '/api/utilities/numberTypes/update', method: 'POST', body: data }),
        }),
        getNumberType: builder.query({
            query: (id) => ({ url: `/api/utilities/numberTypes/${id}`, method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        deleteNumberType: builder.mutation({
            query: (id) => ({ url: `/api/utilities/numberTypes/delete/${id}`, method: 'DELETE' }),
        }),
        restoreNumberType: builder.mutation({
            query: (id) => ({
                url: '/api/utilities/numberTypes/undelete',
                method: 'POST',
                body: toFormData({ id }),
            }),
        }),
        getFieldsBySiteId: builder.query({
            query: (siteId) => ({ url: `/api/utilities/numberTypeField/list/${siteId}`, method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        addNumberTypeFieldOrders: builder.mutation({
            query: ({ numberTypeId, numberTypeFieldId, sortOrder }) => ({
                url: '/api/utilities/numberTypeFieldOrders/create',
                method: 'POST',
                body: toFormData({ numberTypeId, numberTypeFieldId, sortOrder }),
            }),
        }),
        deleteFieldSortOrder: builder.mutation({
            query: (id) => ({
                url: `/api/utilities/numberTypeFieldOrders/delete/${id}`,
                method: 'DELETE',
            }),
        }),
        numberTypeNameValid: builder.query({
            query: ({ numberTypeName, numberTypeId, siteId }) => ({
                url: '/api/utilities/numberTypeNameValid/',
                method: 'GET',
                params: { numberTypeName, numberTypeId, siteId },
            }),
            keepUnusedDataFor: 0,
        }),
        accountsNumberType: builder.query({
            query: () => ({ url: '/api/utilities/numberTypes/accounts', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        sitesNumberType: builder.query({
            query: () => ({ url: '/api/utilities/numberTypes/sites', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        changeSortOrderForNumberTypeFieldOrder: builder.mutation({
            query: ({ numberTypeFieldOrderId, sortOrder }) => ({
                url: '/api/utilities/numberTypeFieldOrders/sortOrder',
                method: 'POST',
                body: toFormData({ numberTypeFieldOrderId, sortOrder }),
            }),
        }),
    }),
});

/**
 * Runs a `documentNumberTypeApi` query once and releases its cache subscription.
 */
export const runDocumentNumberTypeQuery = (dispatch, endpointName, arg) =>
    runQueryOnce(dispatch, documentNumberTypeApi.endpoints[endpointName], arg);

/**
 * Runs a `documentNumberTypeApi` mutation once and clears its tracked entry.
 */
export const runDocumentNumberTypeMutation = (dispatch, endpointName, arg) =>
    runMutationOnce(dispatch, documentNumberTypeApi.endpoints[endpointName], arg);

/**
 * Table `request` adapter for the shared `getTableData` thunk, which still expects an
 * axios-shaped `{ data }` envelope.
 */
export const runDocumentNumberTypeTableQuery = (dispatch, filter) =>
    runTableQuery(dispatch, documentNumberTypeApi.endpoints.getTableData, filter);

export default documentNumberTypeApi;
